import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PageTableHeader from "../../../../components/pageTableHeader/pageTableHeader";
import CommonTable from "../../../../components/table/table";
import CommonModal from "../../../../components/modals/commonModel";
import CommonForm from "../../../../components/form/form";
import DefaultButton from "../../../../components/buttons/defaultButton";
import BottomDrawer from "../../../common/drawer/bottomDrawer";
import DefaultSearchSelectInput from "../../../../components/inputs/defaultSearchSelectInput";
import DefaultDatePicker from "../../../../components/inputs/defaultDatePicker";
import { 
  HiOutlineClock, 
  HiOutlineUsers,
  HiOutlineExclamationCircle,
  HiOutlineCheckCircle,
  HiOutlineEye
} from "react-icons/hi";
import moment from "moment";
import toast from "react-hot-toast";
import { 
  createShift, 
  updateShift, 
  getShiftList, 
  getShiftDetail,
  deleteShift,
  bulkAssignShift,
  previewShiftAssignment
} from "../../../../../services/workspace/attendanceManagement.service";
import { getUserList } from "../../../../../services/workspace/user.service";
import { useCommonFetch } from "../../../../../hooks/useCommonFetch";

const items = [{ label: "HR Shift Editor", path: "" }];

const ShiftEditor = () => {
  const navigate = useNavigate();
  const [isShiftModalOpen, setIsShiftModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [assignmentPreview, setAssignmentPreview] = useState(null);
  const [effectiveFrom, setEffectiveFrom] = useState(moment().format("YYYY-MM-DD"));
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const {
    loading,
    data: tableData,
    fetchData,
    handleDelete,
  } = useCommonFetch(getShiftList, {
    dataPath: "workspaceShift.data",
    totalPath: "workspaceShift.meta.total",
    errorMessage: "Failed to fetch shifts!",
    debounceDelay: 500,
    dataTransformer: (data) => {
      if (!Array.isArray(data)) return { data: [], action: {} };

      // Store original data array for reference
      const originalDataArray = [...data];

      const transformedData = data.map((item, index) => ({
        id: {
          data: item.uuid || item.id,
          variant: "text",
        },
        name: {
          data: item.name,
          variant: "text",
        },
        start_time: {
          data: item.start_time,
          variant: "text",
        },
        end_time: {
          data: item.end_time,
          variant: "text",
        },
        working_hours: {
          data: `${item.required_working_hour || 0}h`,
          variant: "text",
        },
        employees_count: {
          data: item.total_users || 0,
          variant: "text",
        },
        full_day_threshold: {
          data: `${item.full_day_threshold_pct || 80}%`,
          variant: "text",
        },
        status: {
          data: item.status || "Active",
          variant: "status",
          customCases: {
            "Active": "Active",
            "Inactive": "Inactive",
          },
          background: item.status === "Active" ? "bg-success-50" : "bg-gray-100",
          text: item.status === "Active" ? "text-success-700" : "text-gray-700",
        },
        actions: {
          data: item.uuid || item.id,
          variant: "text",
        },
        // Store original item data for edit
        _originalData: item,
      }));

      const actionButton = {
        view: (item) => {
          // Navigate to shift detail page
          const shiftId = item.id.data;
          navigate(`/hr/shift-editor/${shiftId}`);
        },
        edit: (item) => {
          // Use original data from table instead of fetching from API
          const originalData = item._originalData || originalDataArray.find(d => (d.uuid || d.id) === item.id.data);
          handleEditShift(originalData);
        },
        assign: (item) => {
          // Assign employees to shift
          const shiftId = item.id.data;
          handleBulkAssign(shiftId);
        },
        delete: (item) => {
          handleDelete(item.id.data, deleteShift, {
            successMessage: "Shift deleted successfully!",
            errorMessage: "Failed to delete shift!",
            onSuccess: () => {
              // Ensure table refreshes after deletion
              fetchData({}, true);
            },
          });
        },
      };

      return {
        data: transformedData,
        action: actionButton,
      };
    },
  });

  const tableHeaders = [
    { label: "Shift Name", key: "name" },
    { label: "Start Time", key: "start_time" },
    { label: "End Time", key: "end_time" },
    { label: "Working Hours", key: "working_hours" },
    { label: "Assigned Employees", key: "employees_count" },
    { label: "Full-day Threshold", key: "full_day_threshold" },
    { label: "Status", key: "status" },
  ];

  const shiftFormFields = [
    // Basic Information Section
    {
      name: "name",
      label: "Shift Name",
      type: "text",
      placeholder: "e.g., Morning Shift, Evening Shift",
      required: true,
      colSpan: 12,
    },
    {
      name: "start_time",
      label: "Start Time",
      type: "time",
      required: true,
      colSpan: 4,
    },
    {
      name: "end_time",
      label: "End Time",
      type: "time",
      required: true,
      colSpan: 4,
    },
    {
      name: "working_hours_required",
      label: "Working Hours Required",
      type: "number",
      placeholder: "8",
      required: true,
      min: 1,
      max: 24,
      colSpan: 4,
      info: "Total working hours required per day",
    },
    
    // Attendance Settings Section
    {
      name: "grace_period_minutes",
      label: "Grace Period (minutes)",
      type: "number",
      placeholder: "15",
      required: false,
      min: 0,
      max: 60,
      colSpan: 4,
      info: "Extra time before late mark (e.g., 15 min grace)",
    },
    {
      name: "late_threshold_minutes",
      label: "Late Mark Threshold (minutes)",
      type: "number",
      placeholder: "30",
      required: false,
      min: 0,
      colSpan: 4,
      info: "Minutes after which employee is marked as Late",
    },
    {
      name: "full_day_threshold_pct",
      label: "Full-day Threshold (%)",
      type: "number",
      placeholder: "80",
      required: true,
      min: 0,
      max: 100,
      colSpan: 4,
      info: "Minimum % of working hours for full day mark",
    },
    
    // Break Settings Section
    {
      name: "break_start_time",
      label: "Break Start Time",
      type: "time",
      required: false,
      colSpan: 4,
    },
    {
      name: "break_end_time",
      label: "Break End Time",
      type: "time",
      required: false,
      colSpan: 4,
    },
    {
      name: "break_duration",
      label: "Break Duration (minutes)",
      type: "number",
      placeholder: "30",
      required: false,
      min: 0,
      colSpan: 4,
      disabled: false,
      info: "Auto-calculated from break times, or enter manually",
    },
    
    // Advanced Settings Section
    {
      name: "ot_threshold_minutes",
      label: "OT Threshold (minutes)",
      type: "number",
      placeholder: "480",
      required: false,
      min: 0,
      colSpan: 6,
      info: "Minimum minutes worked to qualify for overtime (e.g., 480 = 8 hours)",
    },
    {
      name: "allow_multiple_in_out",
      label: "Allow Multiple In/Out",
      type: "checkbox",
      required: false,
      colSpan: 6,
      info: "Allow employees to check in/out multiple times in a day",
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      placeholder: "Optional description for this shift (e.g., Standard office hours, includes lunch break)",
      colSpan: 12,
    },
  ];

  const calculateBreakDuration = (start, end) => {
    if (!start || !end) return 0;
    
    try {
      // Parse times in HH:mm format
      const startMoment = moment(start, "HH:mm", true); // strict parsing
      const endMoment = moment(end, "HH:mm", true); // strict parsing
      
      // Validate that times were parsed correctly
      if (!startMoment.isValid() || !endMoment.isValid()) {
        console.error("Invalid time format:", { start, end });
        return 0;
      }
      
      // If end time is before start time, it means the break spans midnight
      // Add 24 hours to end time to handle this case
      if (endMoment.isBefore(startMoment) || endMoment.isSame(startMoment)) {
        endMoment.add(1, "day");
      }
      
      const duration = endMoment.diff(startMoment, "minutes");
      // Ensure duration is non-negative
      return Math.max(0, duration);
    } catch (error) {
      console.error("Error calculating break duration:", error, { start, end });
      return 0;
    }
  };

  // Convert time from HTML time input format (HH:mm) to API format (H:i:s)
  const convertTimeToApiFormat = (time) => {
    if (!time) return "";
    // If already in H:i:s format, return as is
    if (time.includes(":") && time.split(":").length === 3) {
      return time;
    }
    // Convert from HH:mm to H:i:s
    return time ? `${time}:00` : "";
  };

  // Convert time from API format (H:i:s) to HTML time input format (HH:mm)
  const convertTimeFromApiFormat = (time) => {
    if (!time) return "";
    // If already in HH:mm format, return as is
    if (time.includes(":") && time.split(":").length === 2) {
      return time;
    }
    // Convert from H:i:s to HH:mm
    return time ? time.substring(0, 5) : "";
  };

  // Use ref to track previous values and prevent infinite loops
  const prevBreakTimesRef = useRef({ start: "", end: "" });

  // Memoize the onValuesChange callback to prevent infinite re-renders
  const handleValuesChange = useCallback((values, setFieldValue) => {
    const startTime = values.break_start_time || "";
    const endTime = values.break_end_time || "";
    const prevStart = prevBreakTimesRef.current.start;
    const prevEnd = prevBreakTimesRef.current.end;

    // Only recalculate if break times actually changed
    if (startTime !== prevStart || endTime !== prevEnd) {
      prevBreakTimesRef.current = { start: startTime, end: endTime };

      if (startTime && endTime) {
        const calculatedDuration = calculateBreakDuration(startTime, endTime);
        const currentDuration = values.break_duration || 0;
        // Only auto-calculate if break_duration is 0 or empty (not manually set)
        // This allows users to manually override the calculated value
        if (currentDuration === 0 || currentDuration === "" || currentDuration === null) {
          setFieldValue("break_duration", calculatedDuration);
        }
        // If user has manually set a value, don't override it
      } else {
        const currentDuration = values.break_duration || 0;
        // Only update if duration is not already 0
        if (currentDuration !== 0) {
          setFieldValue("break_duration", 0);
        }
      }
    }
  }, []);

  const handleCreateShift = () => {
    setIsEditMode(false);
    setSelectedShift(null);
    prevBreakTimesRef.current = { start: "", end: "" };
    setIsShiftModalOpen(true);
  };

  const handleEditShift = (shiftData) => {
    // Use data directly from table, no API call needed
    if (!shiftData) {
      toast.error("Shift data not found!");
      return;
    }

    // Map table data to form field names
    const formData = {
      id: shiftData.id || shiftData.uuid,
      uuid: shiftData.uuid,
      name: shiftData.name || "",
      start_time: convertTimeFromApiFormat(shiftData.start_time),
      end_time: convertTimeFromApiFormat(shiftData.end_time),
      working_hours_required: shiftData.required_working_hour || shiftData.working_hours_required || 0,
      grace_period_minutes: shiftData.grace_period || shiftData.grace_period_minutes || 0,
      late_threshold_minutes: shiftData.late_mark_threshold || shiftData.late_threshold_minutes || 0,
      ot_threshold_minutes: shiftData.overtime_threshold || shiftData.ot_threshold_minutes || 0,
      full_day_threshold_pct: shiftData.fulltime_threshold || shiftData.full_day_threshold_pct || 0,
      allow_multiple_in_out: shiftData.is_allow_multiple_inout === 1 || shiftData.allow_multiple_in_out || false,
      description: shiftData.description || "",
    };

    // Map break data to form fields (convert from API format to HTML time input format)
    const breakStartTime = convertTimeFromApiFormat(shiftData.break_start_time);
    const breakEndTime = convertTimeFromApiFormat(shiftData.break_end_time);
    
    formData.break_start_time = breakStartTime;
    formData.break_end_time = breakEndTime;
    
    // Use saved break_duration from API, don't recalculate (user may have manually set it)
      formData.break_duration = shiftData.break_duration || 0;

    // Reset break times ref for the new shift to prevent auto-calculation on load
    // Set the refs to match the loaded times so handleValuesChange doesn't trigger
    prevBreakTimesRef.current = { 
      start: breakStartTime || "", 
      end: breakEndTime || "" 
    };

    setSelectedShift(formData);
    setIsEditMode(true);
    setIsShiftModalOpen(true);
  };

  const handleShiftSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      // Use manually entered break_duration if provided, otherwise calculate from times
      let breakDuration = 0;
      if (values.break_duration && values.break_duration > 0) {
        // Use manually entered duration
        breakDuration = parseInt(values.break_duration) || 0;
      } else if (values.break_start_time && values.break_end_time) {
        // Auto-calculate from break times if duration not manually set
        breakDuration = calculateBreakDuration(values.break_start_time, values.break_end_time);
      }

      // Map form fields to API payload format (convert time format from HH:mm to H:i:s)
      const payload = {
        name: values.name,
        start_time: convertTimeToApiFormat(values.start_time),
        end_time: convertTimeToApiFormat(values.end_time),
        required_working_hour: parseInt(values.working_hours_required) || 0,
        grace_period: parseInt(values.grace_period_minutes) || 0,
        late_mark_threshold: parseInt(values.late_threshold_minutes) || 0,
        overtime_threshold: parseInt(values.ot_threshold_minutes) || 0,
        fulltime_threshold: parseInt(values.full_day_threshold_pct) || 0,
        is_allow_multiple_inout: values.allow_multiple_in_out ? 1 : 0,
        description: values.description || "",
        break_start_time: convertTimeToApiFormat(values.break_start_time),
        break_end_time: convertTimeToApiFormat(values.break_end_time),
        break_duration: breakDuration,
      };

      if (isEditMode && (selectedShift?.uuid || selectedShift?.id)) {
        const shiftId = selectedShift.uuid || selectedShift.id;
        await updateShift(shiftId, payload);
        toast.success("Shift updated successfully!");
      } else {
        await createShift(payload);
        toast.success("Shift created successfully!");
      }

      // Close drawer and reset form
      setIsShiftModalOpen(false);
      resetForm();
      setSelectedShift(null);
      setIsEditMode(false);
      prevBreakTimesRef.current = { start: "", end: "" };
      
      // Reload table data
      fetchData({}, true);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save shift!");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkAssign = (shiftId) => {
    setSelectedShift({ id: shiftId });
    setSelectedEmployees([]);
    setAssignmentPreview(null);
    setIsAssignModalOpen(true);
  };

  const handlePreviewAssignment = async () => {
    if (!selectedShift?.id || selectedEmployees.length === 0) {
      toast.error("Please select employees");
      return;
    }

    setLoadingPreview(true);
    try {
      // Preview API - if available, otherwise skip preview
      try {
        const preview = await previewShiftAssignment(selectedShift.id, {
          employee_ids: selectedEmployees.map(e => e.value),
          effective_from: effectiveFrom,
        });
        setAssignmentPreview(preview);
      } catch (previewError) {
        // Preview API might not be available, continue without preview
        setAssignmentPreview(null);
      }
    } catch (error) {
      toast.error("Failed to preview assignment");
      console.error(error);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleConfirmAssignment = async () => {
    if (!selectedShift?.id || selectedEmployees.length === 0) {
      return;
    }

    try {
      // Use PUT API with users array (user IDs)
      // API expects: { effective_from: "YYYY-MM-DD", users: [2, 3, 4] }
      const payload = {
        effective_from: effectiveFrom,
        users: selectedEmployees.map(e => {
          // Extract user ID - API expects numeric IDs
          const employee = e.employee || {};
          const userId = employee.id || employee.user_id || e.value;
          // Convert to number if it's a string
          return typeof userId === 'string' ? parseInt(userId) : userId;
        }).filter(id => id != null), // Remove any null/undefined values
      };

      await bulkAssignShift(selectedShift.id, payload);
      toast.success(`Successfully assigned ${selectedEmployees.length} employees to shift`);
      setIsAssignModalOpen(false);
      setSelectedEmployees([]);
      setAssignmentPreview(null);
      setEffectiveFrom(moment().format("YYYY-MM-DD"));
      
      // Reload table data to show updated employee count
      fetchData({}, true);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to assign employees");
      console.error(error);
    }
  };

  // Fetch employee list for shift assignment
  const fetchEmployeeList = useCallback(async () => {
    setLoadingEmployees(true);
    try {
      const response = await getUserList({
        with: "department,designation",
        per_page: 1000, // Fetch all employees
      });
      
      const employees = response?.user?.data || [];
      const options = employees.map((emp) => ({
        value: emp.id || emp.user_id || emp.uuid, // Use id as primary (for API)
        label: `${emp.name || "Unknown"} (${emp.code || "N/A"})`,
        employee: emp, // Store full employee object for reference
      }));
      
      setEmployeeOptions(options);
    } catch (error) {
      console.error("Failed to fetch employees:", error);
      toast.error("Failed to load employee list");
      setEmployeeOptions([]);
    } finally {
      setLoadingEmployees(false);
    }
  }, []);

  // Fetch employees when assign modal opens
  useEffect(() => {
    if (isAssignModalOpen) {
      fetchEmployeeList();
    }
  }, [isAssignModalOpen, fetchEmployeeList]);

  return (
    <div className="flex flex-col gap-4">
      <PageTableHeader
        breadcrumbs={items}
        add
        onClickAdd={handleCreateShift}
        isSearch
      />

      <div className="bg-white rounded-md shadow-sm">
        <CommonTable
          TableHeaderData={tableHeaders}
          tabledata={tableData.data}
          loading={loading}
          action={tableData.action}
        />
      </div>

      {/* Create/Edit Shift Bottom Drawer */}
      <BottomDrawer
        isOpen={isShiftModalOpen}
        setIsOpen={() => {
          setIsShiftModalOpen(false);
          setSelectedShift(null);
          setIsEditMode(false);
          prevBreakTimesRef.current = { start: "", end: "" };
        }}
        title={isEditMode ? "Edit Shift" : "Create Shift"}
        classNames="!w-[800px]"
        position="right"
      >
        <div className="flex flex-col gap-6 overflow-y-auto px-2 py-4">
          <CommonForm
            fields={shiftFormFields}
            initialData={selectedShift || {}}
            onSubmit={handleShiftSubmit}
            submitButtonText={isEditMode ? "Update Shift" : "Create Shift"}
            columnsPerRow={2}
            gap="gap-4"
            onValuesChange={handleValuesChange}
            fieldClassName="mb-4"
            rowClassName="mb-6"
          />
        </div>
      </BottomDrawer>

      {/* Bulk Assign Modal */}
      <CommonModal
        modalSize="2xl"
        headerTitle="Bulk Assign Employees to Shift"
        onClose={() => {
          setIsAssignModalOpen(false);
          setSelectedEmployees([]);
          setAssignmentPreview(null);
        }}
        show={isAssignModalOpen}
      >
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="flex flex-col">
            <DefaultSearchSelectInput
              label="Select Employees"
              options={employeeOptions}
              value={selectedEmployees}
              onChange={setSelectedEmployees}
              placeholder={loadingEmployees ? "Loading employees..." : "Search and select employees"}
              isMulti={true}
              required
              isLoading={loadingEmployees}
              disabled={loadingEmployees}
                customClass="[&_.react-select__value-container]:max-h-[200px] [&_.react-select__value-container]:overflow-y-auto"
            />
            </div>
            <DefaultDatePicker
              label="Effective From"
              value={effectiveFrom}
              onChange={(date) => setEffectiveFrom(moment(date).format("YYYY-MM-DD"))}
              minDate={moment().format("YYYY-MM-DD")}
              required
            />
          </div>

          <DefaultButton
            name="Preview Assignment"
            variant="primary"
            onClick={handlePreviewAssignment}
            disabled={selectedEmployees.length === 0}
          />

          {loadingPreview && (
            <div className="text-center py-4">
              <p className="text-textLight">Loading preview...</p>
            </div>
          )}

          {assignmentPreview && (
            <div className="border border-borderColor rounded-md p-4">
              <h5 className="text-sm font-semibold text-theme mb-3">Assignment Preview</h5>
              <div className="flex flex-col gap-2">
                <div className="flex justify-between">
                  <span className="text-sm text-textLight">Employees to assign:</span>
                  <span className="font-medium">{assignmentPreview.assigned_count || selectedEmployees.length}</span>
                </div>
                {assignmentPreview.conflicts && assignmentPreview.conflicts.length > 0 && (
                  <div className="mt-3">
                    <div className="flex items-center gap-2 text-warning-700 mb-2">
                      <HiOutlineExclamationCircle />
                      <span className="text-sm font-semibold">Conflicts Found:</span>
                    </div>
                    <div className="bg-warning-50 border border-warning-200 rounded-md p-3">
                      <ul className="text-sm space-y-1">
                        {assignmentPreview.conflicts.map((conflict, idx) => (
                          <li key={idx} className="text-warning-800">
                            {conflict.employee_name}: {conflict.conflict_desc}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
                {(!assignmentPreview.conflicts || assignmentPreview.conflicts.length === 0) && (
                  <div className="flex items-center gap-2 text-success-700 mt-2">
                    <HiOutlineCheckCircle />
                    <span className="text-sm">No conflicts found</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="bg-primary-50 border border-borderColor rounded-md p-4">
            <p className="text-sm text-textLight">
              <strong>Note:</strong> This action will assign {selectedEmployees.length} employees to the shift from {moment(effectiveFrom).format("MMMM DD, YYYY")}. 
              Existing assignments for the same period will remain (creates effective-dated entries).
            </p>
          </div>

          <div className="flex gap-2 justify-end">
            <DefaultButton
              name="Cancel"
              variant="secondary"
              onClick={() => setIsAssignModalOpen(false)}
            />
            <DefaultButton
              name="Confirm & Assign"
              variant="primary"
              onClick={handleConfirmAssignment}
              disabled={selectedEmployees.length === 0 || loadingPreview}
            />
          </div>
        </div>
      </CommonModal>
    </div>
  );
};

export default ShiftEditor;

